import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { getFrequencyMagic, ackFrequencyMagic, queryFrequencyMagic } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { CheckCircle2, Megaphone, MessageCircleQuestion, Loader2, HelpCircle } from 'lucide-react';

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EEF2FB] via-[#F4F7FD] to-[#E8EEFA] dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-[#0B1220] via-[#15233B] to-[#2563EB] flex items-center justify-center shadow-lg">
            <Megaphone className="h-6 w-6 text-white drop-shadow" />
          </div>
          <div>
            <p className="font-heading text-xl font-bold">The Frequency</p>
            <p className="text-xs text-muted-foreground">United Group Internal Communications</p>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

export function FrequencyAckPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acked, setAcked] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { (async () => {
    try {
      const r = await getFrequencyMagic(token);
      setInfo(r.data);
      setAcked(r.data.already_acknowledged);
    } catch (e) { setError(e.response?.data?.detail || 'Invalid link'); }
    finally { setLoading(false); }
  })(); }, [token]);

  const doAck = async () => {
    try {
      await ackFrequencyMagic(token);
      setAcked(true);
      toast.success('Thank you — your acknowledgement is recorded.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <Shell><div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#2563EB]" /></div></Shell>;
  if (error) return <Shell><Card><CardContent className="p-8 text-center text-rose-500" data-testid="ack-error">{error}</CardContent></Card></Shell>;
  return (
    <Shell>
      {info?.content_html ? (
        <Card className="mb-4 overflow-hidden"><CardContent className="p-0">
          <iframe srcDoc={info.content_html} className="w-full bg-white" style={{ height: '60vh', border: '0' }} title="newsletter" data-testid="ack-content-iframe" />
        </CardContent></Card>
      ) : null}
      <Card><CardContent className="p-8 text-center space-y-4">
        <CheckCircle2 className={`h-16 w-16 mx-auto ${acked ? 'text-emerald-500' : 'text-muted-foreground'}`} />
        <h1 className="text-2xl font-bold">{info?.edition?.title}</h1>
        <p className="text-sm text-muted-foreground">{info?.edition?.period}</p>
        {acked ? (
          <p className="text-emerald-600 font-medium" data-testid="ack-done">✓ Your acknowledgement has been recorded.</p>
        ) : (
          <>
            <p className="text-sm">By clicking below you confirm:</p>
            <p className="italic text-base">&quot;I have read and understood this communication.&quot;</p>
            <Button onClick={doAck} className="bg-gradient-to-r from-[#2563EB] to-[#1E40AF] text-white font-semibold hover:opacity-90 px-8" data-testid="confirm-ack-btn">Acknowledge Receipt</Button>
          </>
        )}
        <div className="pt-2">
          <a href={`/frequency/query/${token}`} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1E3A5F] underline underline-offset-2 hover:opacity-80" data-testid="ack-raise-query-btn">
            <HelpCircle className="h-4 w-4" /> Have a question? Raise a Query
          </a>
        </div>
        <p className="text-xs text-muted-foreground pt-4">Signed in as: <strong>{info?.email}</strong></p>
      </CardContent></Card>
    </Shell>
  );
}

export function FrequencyQueryPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ subject: '', description: '', department_id: '' });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { (async () => {
    try { const r = await getFrequencyMagic(token); setInfo(r.data); }
    catch (e) { setError(e.response?.data?.detail || 'Invalid link'); }
    finally { setLoading(false); }
  })(); }, [token]);

  const submit = async () => {
    if (!form.subject.trim() || !form.description.trim()) { toast.error('Please fill subject + description'); return; }
    try {
      await queryFrequencyMagic(token, { subject: form.subject, description: form.description, department_id: form.department_id || null });
      setSubmitted(true);
      toast.success('Query submitted — the team will respond shortly.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <Shell><div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div></Shell>;
  if (error) return <Shell><Card><CardContent className="p-8 text-center text-rose-500" data-testid="query-error">{error}</CardContent></Card></Shell>;
  const depts = info?.edition_departments || [];
  return (
    <Shell>
      <Card><CardContent className="p-8 space-y-4">
        <div className="flex items-center gap-2">
          <MessageCircleQuestion className="h-6 w-6 text-[#2563EB]" />
          <h1 className="text-2xl font-bold">Raise a Query</h1>
        </div>
        <p className="text-sm text-muted-foreground">About: <strong>{info?.edition?.title}</strong> ({info?.edition?.period})</p>
        {submitted ? (
          <div className="text-center py-6 space-y-2" data-testid="query-success">
            <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" />
            <p className="font-medium text-emerald-600">Your query has been submitted.</p>
            <p className="text-sm text-muted-foreground">Marketing will route it to the right department and you&apos;ll receive a response by email.</p>
          </div>
        ) : (
          <>
            <div>
              <Label>Which department does this query concern?</Label>
              <select
                value={form.department_id}
                onChange={(e) => setForm({ ...form, department_id: e.target.value })}
                className="mt-1 block w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                data-testid="query-dept-select"
              >
                <option value="">— Let Marketing route it —</option>
                {depts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              <p className="text-xs text-muted-foreground mt-1">Pick the department your query is about, or leave blank for Marketing to route it.</p>
            </div>
            <div><Label>Subject *</Label><Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="One-line summary" data-testid="query-subject-input" /></div>
            <div><Label>Query Description *</Label><Textarea rows={6} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Provide details…" data-testid="query-desc-input" /></div>
            <Button onClick={submit} className="bg-gradient-to-r from-[#2563EB] to-[#1E40AF] text-white font-semibold w-full" data-testid="submit-query-btn">Submit Query</Button>
          </>
        )}
        <p className="text-xs text-muted-foreground pt-2">Signed in as: <strong>{info?.email}</strong></p>
      </CardContent></Card>
    </Shell>
  );
}
